import { type SiloHalfStatic, type SiloMarketEntry } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createBaseTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { isFailedCall } from '../../../utils/multicall'
import { decodeLensRows } from '../position-lens'
import {
  SILO_V2_USER_CALLS_PER_SIDE,
  SILO_V2_USER_CALLS_PER_PAIR,
  SILO_LENS_WORDS,
  usesSiloLens,
} from './userCallBuild'
import { resolveSiloRoster } from './roster'

/** One pair's ten raw values, however they were read. */
interface SiloPairState {
  /** [collateralShares, protectedShares, collateralRate, protectedRate, maxRepay] per side. */
  side0: bigint[]
  side1: bigint[]
}

/**
 * Returns a [converter, expectedCallCount] tuple for Silo v2 user data.
 *
 * Result layout (10 slots per pair, 5 per side):
 *   [collateralShares0, protectedShares0, collateralRate0, protectedRate0, maxRepay0,
 *    collateralShares1, protectedShares1, collateralRate1, protectedRate1, maxRepay1]
 *
 * Deposits are computed off-chain per deposit type with separate rates:
 *   collateralAssets = collateralShares * collateralRate / 10^decimals
 *   protectedAssets  = protectedShares  * protectedRate  / 10^decimals
 *   totalDeposits    = collateralAssets + protectedAssets
 *
 * Rates differ because collateral deposits accrue borrower interest while
 * protected deposits don't.
 *
 * `collateralEnabled` is set to `true` when the silo has deposits and its
 * `lt > 0` (its side can back borrows on the other silo).
 *
 * Aggregation goes through `createBaseTypeUserState` using config mode `'0'`.
 */
export const getSiloV2UserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
  params?: any,
): [
  (data: any[]) => { [lender: string]: UserData } | undefined,
  number,
] => {
  const roster = resolveSiloRoster(chainId, lender, params)
  const viaLens = usesSiloLens(chainId)
  const expected = roster.length === 0
    ? 0
    : viaLens
      ? 1
      : roster.length * SILO_V2_USER_CALLS_PER_PAIR

  return [
    (data: any[]) => {
      if (expected === 0) return undefined
      if (!data || data.length !== expected) return undefined

      /**
       * Pair index → its ten values.
       *
       * A pair ABSENT from the map is an answer, not a gap: either the per-pair
       * walk's slice was not readable, or the lens probed the pair's six share
       * balances and found nothing. Both mean no row, which is what the old
       * per-pair converter did by returning `undefined` for an empty pair.
       */
      let states: Map<number, SiloPairState>
      if (viaLens) {
        const blobs = data[0]
        if (isFailedCall(blobs) || !Array.isArray(blobs) || blobs.length !== 1) {
          return undefined
        }
        // Silo rows carry NO flags byte — `PositionLens.silo` emits `uint16 index`
        // followed by ten bare words. A fork found this: the generic decoder
        // assumed the Compound/LlamaLend stride and rejected every real blob.
        const { rows } = decodeLensRows(blobs[0], SILO_LENS_WORDS, 0, 0)
        states = new Map(
          rows.map((r) => [
            r.index,
            { side0: r.words.slice(0, 5), side1: r.words.slice(5, 10) },
          ]),
        )
      } else {
        states = new Map()
        for (let i = 0; i < roster.length; i++) {
          const base = i * SILO_V2_USER_CALLS_PER_PAIR
          const slice = data.slice(base, base + SILO_V2_USER_CALLS_PER_PAIR)
          if (slice.some((v) => isFailedCall(v))) continue
          states.set(i, {
            side0: slice.slice(0, 5).map(toBigInt),
            side1: slice.slice(5, 10).map(toBigInt),
          })
        }
      }

      const out: { [lender: string]: UserData } = {}
      roster.forEach(({ key, market }, i) => {
        const st = states.get(i)
        if (!st) return
        const entry = convertSiloPair(
          chainId,
          key,
          account,
          market,
          st,
          meta?.[key],
        )
        if (entry) out[key] = entry
      })
      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}

/** One pair → one `UserData`, keyed under its own per-pair lender key. */
function convertSiloPair(
  chainId: string,
  lender: string,
  account: string,
  market: SiloMarketEntry,
  state: SiloPairState,
  metaMap?: LenderCrossPoolMeta,
): UserData | undefined {
  {
    {
      const sides: Array<{ self: SiloHalfStatic; other: SiloHalfStatic }> = [
        { self: market.silo0, other: market.silo1 },
        { self: market.silo1, other: market.silo0 },
      ]

      const lendingPositions: Record<string, any> = {}
      let addedDeposits = 0
      let addedDebt = 0
      let hasAnyPosition = false

      for (let s = 0; s < 2; s++) {
        const { self } = sides[s]
        const vals = s === 0 ? state.side0 : state.side1
        const [
          collateralShares,
          protectedShares,
          collateralRate,
          protectedRate,
          repayRaw,
        ] = vals

        // Convert each type separately: assets = shares * rate / 10^decimals
        const decimals = self.decimals
        const oneUnit = BigInt(10) ** BigInt(decimals)
        const collateralAssets =
          oneUnit > 0n ? (collateralShares * collateralRate) / oneUnit : 0n
        const protectedAssets =
          oneUnit > 0n ? (protectedShares * protectedRate) / oneUnit : 0n
        const depositAssetsRaw = collateralAssets + protectedAssets

        // Skip positions with zero balance
        if (depositAssetsRaw === 0n && repayRaw === 0n) continue
        hasAnyPosition = true

        const marketUid = createMarketUid(chainId, lender, self.silo)
        const meta = metaMap?.[marketUid]
        const metaDecimals = meta?.asset?.decimals ?? decimals

        const depositsStr = parseRawAmount(
          depositAssetsRaw.toString(),
          metaDecimals,
        )
        const depositsStandardStr = parseRawAmount(
          collateralAssets.toString(),
          metaDecimals,
        )
        const depositsProtectedStr = parseRawAmount(
          protectedAssets.toString(),
          metaDecimals,
        )
        const debtStr = parseRawAmount(repayRaw.toString(), metaDecimals)
        const depositsNum = Number(depositsStr)
        const debtNum = Number(debtStr)

        const displayPrice = meta ? getDisplayPrice(meta) : 0
        const oraclePrice = meta ? getOraclePrice(meta) : 0
        const priceHist = meta?.price?.priceUsd24h ?? displayPrice

        const depositsUSD = depositsNum * displayPrice
        const debtUSD = debtNum * displayPrice
        const depositsUSDOracle = depositsNum * oraclePrice
        const debtUSDOracle = debtNum * oraclePrice

        addedDeposits += depositsNum * priceHist
        addedDebt += debtNum * priceHist

        // collateralEnabled: this silo's deposits can back borrows on the
        // OTHER side iff self.lt > 0 AND the user actually has deposits.
        const selfLt = Number(self.lt) / 1e18
        const collateralEnabled = selfLt > 0 && depositAssetsRaw > 0n

        lendingPositions[marketUid] = {
          marketUid,
          underlying: self.token.toLowerCase(),
          deposits: depositsStr,
          depositsStandard: depositsStandardStr,
          depositsProtected: depositsProtectedStr,
          debt: debtStr,
          debtStable: '0',
          // Raw share balances (stringified bigints, not decimal-formatted).
          // Used by the leverage close flow to approve the exact share count
          // and avoid rounding errors on "withdraw all".
          depositShares: collateralShares.toString(),
          protectedDepositShares: protectedShares.toString(),
          depositsUSD,
          debtUSD,
          debtStableUSD: 0,
          depositsUSDOracle,
          debtUSDOracle,
          debtStableUSDOracle: 0,
          stableBorrowRate: '0',
          collateralEnabled,
          collateralRate: collateralRate.toString(),
          claimableRewards: 0,
        }
      }

      if (!hasAnyPosition) return undefined

      const payload = {
        chainId,
        account,
        lendingPositions,
        rewards: [],
        userEMode: 0,
      }

      return createBaseTypeUserState(
        payload,
        metaMap!,
        addedDeposits,
        addedDebt,
        lender,
      )
    }
  }
}

function toBigInt(v: any): bigint {
  if (v === undefined || v === null || v === '0x') return 0n
  if (typeof v === 'bigint') return v
  try {
    return BigInt(v as any)
  } catch {
    return 0n
  }
}
